import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import consultantApi from '../../services/consultantApi';
import './JobManagement.css';
import '../Employer/Employer.css';
import '../Employer/MyJobs.css';

const JobManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [jobs, setJobs] = useState({ draft: [], pending_approval: [], approved: [], rejected: [] });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    fetchMyJobs();
  }, []);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.mj-dropdown-wrap')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  const fetchMyJobs = async () => {
    try {
      setLoading(true);
      const response = await consultantApi.getMyJobs();
      // Backend returns nested data structure: response.data.data
      setJobs(response.data.data || response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
      console.error('Fetch jobs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishJob = async (jobId) => {
    try {
      await consultantApi.publishJob(jobId);
      toast.success('Job published successfully');
      fetchMyJobs();
    } catch (error) {
      toast.error(error.message || 'Failed to publish job');
      console.error('Publish job error:', error);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await consultantApi.updateJobStatus(jobId, newStatus);
      toast.success(`Job status changed to ${newStatus === 'on_hold' ? 'On Hold' : newStatus}`);
      fetchMyJobs();
    } catch (error) {
      toast.error(error?.error?.message || error?.message || 'Failed to update job status');
      console.error('Update job status error:', error);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }
    try {
      await consultantApi.deleteJob(jobId);
      toast.success('Job deleted successfully');
      fetchMyJobs();
    } catch (error) {
      toast.error(error?.error?.message || error?.message || 'Failed to delete job');
      console.error('Delete job error:', error);
    }
  };

  const handleEditJob = (jobId) => {
    navigate(`/consultant/jobs/edit/${jobId}`);
  };

  const toggleDropdown = (jobId) => {
    setActiveDropdown(activeDropdown === jobId ? null : jobId);
  };

  const handleActionClick = (action, job) => {
    const jobId = job._id;
    setActiveDropdown(null);
    switch (action) {
      case 'edit': handleEditJob(jobId); break;
      case 'applications': navigate('/consultant/applications'); break;
      case 'view': navigate(`/jobs/${jobId}`); break;
      case 'publish': handlePublishJob(jobId); break;
      case 'hold': handleStatusChange(jobId, 'on_hold'); break;
      case 'close': handleStatusChange(jobId, 'closed'); break;
      case 'reopen': handleStatusChange(jobId, 'active'); break;
      case 'delete': handleDelete(jobId); break;
      default: break;
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  const approvalConfig = {
    draft:            { label: 'Draft',            color: 'gray',   icon: 'edit_note' },
    pending_approval: { label: 'Pending Approval', color: 'orange', icon: 'schedule' },
    approved:         { label: 'Published',        color: 'green',  icon: 'check_circle' },
    rejected:         { label: 'Rejected',         color: 'red',    icon: 'cancel' },
  };

  const getApprovalCfg = (s) => approvalConfig[s] || approvalConfig.draft;

  const jobStatusConfig = {
    on_hold: { label: 'On Hold', color: 'orange', icon: 'pause_circle' },
    closed:  { label: 'Closed',  color: 'red',    icon: 'lock' },
  };

  const allJobs = [
    ...(jobs.draft || []),
    ...(jobs.pending_approval || []),
    ...(jobs.approved || []),
    ...(jobs.rejected || [])
  ];

  // Unique companies derived from loaded jobs (for the company filter)
  const companyOptions = [];
  const seenCompanies = new Set();
  allJobs.forEach(j => {
    const id = j.companyId?._id || j.companyId;
    if (id && !seenCompanies.has(id)) {
      seenCompanies.add(id);
      companyOptions.push({ _id: id, name: j.companyId?.name || 'Company' });
    }
  });

  const getJobsForTab = () => {
    let tabJobs = activeTab === 'all' ? allJobs : (jobs[activeTab] || []);

    // Company filter: 'self' = jobs posted under own name (no companyId)
    if (companyFilter === 'self') {
      tabJobs = tabJobs.filter(j => !j.companyId);
    } else if (companyFilter !== 'all') {
      tabJobs = tabJobs.filter(j => (j.companyId?._id || j.companyId) === companyFilter);
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) return tabJobs;
    return tabJobs.filter(job => job.title?.toLowerCase().includes(query));
  };

  const filteredJobs = getJobsForTab();

  const tabList = [
    { key: 'all',              label: 'All Jobs',  count: allJobs.length,                    dot: '' },
    { key: 'draft',            label: 'Draft',     count: jobs.draft?.length || 0,            dot: 'gray' },
    { key: 'pending_approval', label: 'Pending',   count: jobs.pending_approval?.length || 0, dot: 'orange' },
    { key: 'approved',         label: 'Published', count: jobs.approved?.length || 0,         dot: 'green' },
    { key: 'rejected',         label: 'Rejected',  count: jobs.rejected?.length || 0,         dot: 'red' },
  ];

  const renderJobItem = (job) => {
    const cfg = getApprovalCfg(job.approvalStatus);
    const statusCfg = jobStatusConfig[job.status];
    const canEdit = job.approvalStatus !== 'approved';
    const isLive = job.approvalStatus === 'approved' && job.status === 'active';

    return (
      <div key={job._id} className="mj-item">
        <div className="mj-item-left">
          <div className="mj-item-icon">
            <span className="material-symbols-outlined">work</span>
          </div>
          <div className="mj-item-body">
            <div className="mj-item-title-row">
              <span className="mj-item-title">{job.title}</span>
              <span className={`mj-status-badge ${cfg.color}`}>
                <span className="material-symbols-outlined">{cfg.icon}</span>
                {cfg.label}
              </span>
              {job.approvalStatus === 'approved' && statusCfg && (
                <span className={`mj-status-badge ${statusCfg.color}`}>
                  <span className="material-symbols-outlined">{statusCfg.icon}</span>
                  {statusCfg.label}
                </span>
              )}
              {job.isFeatured && <span className="mj-badge-featured"><span className="material-symbols-outlined">star</span>Featured</span>}
              {job.isUrgent && <span className="mj-badge-urgent"><span className="material-symbols-outlined">priority_high</span>Urgent</span>}
            </div>
            <div className="mj-item-meta">
              <span><span className="material-symbols-outlined">business</span>{job.companyId?.name || 'Independent'}</span>
              {job.jobType?.name && (
                <span><span className="material-symbols-outlined">business_center</span>{job.jobType.name}</span>
              )}
              {job.location?.city && (
                <span><span className="material-symbols-outlined">location_on</span>{job.location.city?.name || job.location.city}</span>
              )}
              <span><span className="material-symbols-outlined">schedule</span>Posted {getTimeAgo(job.createdAt)}</span>
            </div>
            {job.rejectionReason && (
              <div className="mj-rejection-note">
                <span className="material-symbols-outlined">error</span>
                <span><strong>Reason:</strong> {job.rejectionReason}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mj-item-right">
          <div className="mj-item-counters">
            <div className="mj-counter">
              <span className="material-symbols-outlined">visibility</span>
              <strong>{job.views || 0}</strong>
              <span>Views</span>
            </div>
            <div className="mj-counter">
              <span className="material-symbols-outlined">group</span>
              <strong>{job.applications || 0}</strong>
              <span>Applied</span>
            </div>
          </div>

          <div className="mj-item-actions">
            {canEdit && (
              <button className="mj-action-btn" onClick={() => handleEditJob(job._id)} title="Edit">
                <span className="material-symbols-outlined">edit</span>
              </button>
            )}
            <button className="mj-action-btn" onClick={() => handleActionClick('applications', job)} title="Applications">
              <span className="material-symbols-outlined">description</span>
            </button>
            <div className="mj-dropdown-wrap">
              <button className="mj-action-btn" onClick={() => toggleDropdown(job._id)} title="More">
                <span className="material-symbols-outlined">more_vert</span>
              </button>
              {activeDropdown === job._id && (
                <div className="mj-dropdown">
                  {isLive && (
                    <button className="mj-dd-item" onClick={() => handleActionClick('view', job)}>
                      <span className="material-symbols-outlined">open_in_new</span>View Live
                    </button>
                  )}
                  {(job.approvalStatus === 'draft' || job.approvalStatus === 'rejected') && (
                    <button className="mj-dd-item success" onClick={() => handleActionClick('publish', job)}>
                      <span className="material-symbols-outlined">publish</span>Publish
                    </button>
                  )}
                  {job.approvalStatus === 'approved' && job.status === 'active' && (<>
                    <button className="mj-dd-item warning" onClick={() => handleActionClick('hold', job)}>
                      <span className="material-symbols-outlined">pause_circle</span>Put on Hold
                    </button>
                    <button className="mj-dd-item secondary" onClick={() => handleActionClick('close', job)}>
                      <span className="material-symbols-outlined">lock</span>Close Job
                    </button>
                  </>)}
                  {job.approvalStatus === 'approved' && job.status === 'on_hold' && (<>
                    <button className="mj-dd-item success" onClick={() => handleActionClick('reopen', job)}>
                      <span className="material-symbols-outlined">play_circle</span>Activate
                    </button>
                    <button className="mj-dd-item secondary" onClick={() => handleActionClick('close', job)}>
                      <span className="material-symbols-outlined">lock</span>Close Job
                    </button>
                  </>)}
                  {job.approvalStatus === 'approved' && job.status === 'closed' && (
                    <button className="mj-dd-item success" onClick={() => handleActionClick('reopen', job)}>
                      <span className="material-symbols-outlined">lock_open</span>Reopen
                    </button>
                  )}
                  <div className="mj-dd-divider"></div>
                  <button className="mj-dd-item danger" onClick={() => handleActionClick('delete', job)}>
                    <span className="material-symbols-outlined">delete</span>Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="emp-my-jobs mj-container">
        <div className="mj-loading">
          <div className="mj-spinner"></div>
          <p>Loading jobs…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="emp-my-jobs mj-container">
      {/* Header */}
      <div className="mj-header">
        <div>
          <h1 className="mj-title">Jobs</h1>
          <p className="mj-subtitle">Create and manage job postings for your clients</p>
        </div>
        <button className="mj-cta-btn" onClick={() => navigate('/consultant/jobs/create')}>
          <span className="material-symbols-outlined">add</span>
          Create Job
        </button>
      </div>

      {/* Toolbar */}
      <div className="mj-toolbar">
        <form onSubmit={(e) => e.preventDefault()} className="mj-search">
          <span className="material-symbols-outlined">search</span>
          <input
            type="text"
            placeholder="Search by title…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        <select
          className="mj-company-select"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          title="Filter by company"
        >
          <option value="all">All Companies</option>
          <option value="self">Self / Independent</option>
          {companyOptions.map(c => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>

        <select
          className="mj-company-select"
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          title="Filter by approval status"
        >
          {tabList.map(t => (
            <option key={t.key} value={t.key}>{t.label} ({t.count})</option>
          ))}
        </select>
      </div>

      {/* Results count */}
      {filteredJobs.length > 0 && (
        <p className="mj-results-info">
          Showing {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <div className="mj-empty">
          <span className="material-symbols-outlined">work_off</span>
          <h3>No jobs found</h3>
          <p>Create your first job posting to get started</p>
          <button className="mj-cta-btn" onClick={() => navigate('/consultant/jobs/create')}>
            <span className="material-symbols-outlined">add</span>Create Job
          </button>
        </div>
      ) : (
        <div className="mj-list">
          {filteredJobs.map(renderJobItem)}
        </div>
      )}
    </div>
  );
};

export default JobManagement;
